import { formatDurationToHrMin } from "@utils/utils";
import React from "react";

import { AnalyticsStatCard } from "@fronterahealth/frontera-ui-components";

import { BcbaSessionAnalyticsVideosDataType } from "@api/graphql/types-and-hooks";
import { CardContainer } from "@pages/VideoAnalytics/components/CardContainer";
import { BCBA_ANALYTICS_TITLES, calculatePercentageChange } from "@pages/VideoAnalytics/helpers";

interface EngagementMetricsProps {
  currentData: BcbaSessionAnalyticsVideosDataType | null | undefined;
  previousPeriodData: BcbaSessionAnalyticsVideosDataType | null | undefined;
  isFetching: boolean;
  setIsAvgVideosReviewedOpen: (open: boolean) => void;
  setIsPercentageVideosReviewedOpen: (open: boolean) => void;
  setIsReviewCompletionDialogOpen: (open: boolean) => void;
}

export const EngagementMetrics: React.FC<EngagementMetricsProps> = ({
  currentData,
  previousPeriodData,
  isFetching,
  setIsAvgVideosReviewedOpen,
  setIsPercentageVideosReviewedOpen,
  setIsReviewCompletionDialogOpen,
}) => {
  return (
    <CardContainer title="Engagement Metrics">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4">
        <AnalyticsStatCard
          title={BCBA_ANALYTICS_TITLES.AVG_VIDEOS_REVIEWED_PER_BCBA}
          infoTooltip="The average number of videos reviewed by each BCBA."
          value={`${currentData?.averageSessionsReviewedByBcba} videos`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.averageSessionsReviewedByBcba ?? "N/A"} videos` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageSessionsReviewedByBcba ?? 0),
                  Number(previousPeriodData?.averageSessionsReviewedByBcba ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.videosReviewedPercentage ?? 0),
                  Number(previousPeriodData?.videosReviewedPercentage ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsAvgVideosReviewedOpen(true);
          }}
        />
        <AnalyticsStatCard
          title={BCBA_ANALYTICS_TITLES.PERCENTAGE_VIDEOS_REVIEWED}
          infoTooltip="The percentage of uploaded videos that have been reviewed by a BCBA."
          value={`${currentData?.videosReviewedPercentage} %`}
          previousValue={previousPeriodData ? `${previousPeriodData?.videosReviewedPercentage ?? "N/A"} %` : undefined}
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.videosReviewedPercentage ?? 0),
                  Number(previousPeriodData?.videosReviewedPercentage ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.videosReviewedPercentage ?? 0),
                  Number(previousPeriodData?.videosReviewedPercentage ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsPercentageVideosReviewedOpen(true);
          }}
        />
        <AnalyticsStatCard
          title={BCBA_ANALYTICS_TITLES.AVG_REVIEW_COMPLETION_TIME}
          infoTooltip="The average time between a video after being uploaded and a BCBA completing its review."
          value={`${formatDurationToHrMin(currentData?.averageReviewCompletionTime ?? 0)}`}
          previousValue={
            previousPeriodData
              ? `${previousPeriodData ? formatDurationToHrMin(previousPeriodData?.averageReviewCompletionTime ?? 0) : "N/A"}`
              : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageReviewCompletionTime ?? 0),
                  Number(previousPeriodData?.averageReviewCompletionTime ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageReviewCompletionTime ?? 0),
                  Number(previousPeriodData?.averageReviewCompletionTime ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsReviewCompletionDialogOpen(true);
          }}
        />
      </div>
    </CardContainer>
  );
};
